import { motion } from "framer-motion";
import { Zap, ShieldCheck, Briefcase, Link2 } from "lucide-react";

const TIMELINE = [
  { when: "Today", what: "Engage us" },
  { when: "Days 1–14", what: "We audit & file" },
  { when: "Now", what: "Advance funding available", highlight: true },
  { when: "Day 60–90", what: "Full CBP refund" },
];

const BENEFITS = [
  {
    icon: Zap,
    title: "Fast decision",
    body: "Know your advance amount quickly after assessment.",
  },
  {
    icon: ShieldCheck,
    title: "No personal guarantee",
    body: "Advance is secured against your expected refund — not personal assets.",
  },
  {
    icon: Briefcase,
    title: "Working capital relief",
    body: "Ideal if your business needs liquidity now rather than in 90 days.",
  },
  {
    icon: Link2,
    title: "Bundled with recovery",
    body: "The advance and contingency recovery work together seamlessly.",
  },
];

export default function AdvanceFunding() {
  return (
    <section
      id="advance-funding"
      data-testid="advance-funding-section"
      className="relative py-24 sm:py-32 bg-[#F9FAFB]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#0A1936]" />
            <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#0A1936]/70">
              06 · Advance Funding
            </span>
          </div>
          <h2 className="font-serif text-[#0A1936] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Get your money now
            <br />
            <span className="italic">— not in 90 days.</span>
          </h2>
        </motion.div>

        {/* Horizontal timeline */}
        <div
          data-testid="advance-timeline"
          className="relative mb-20 rounded-md border border-[#0A1936]/10 bg-white p-8 lg:p-10"
        >
          <div className="hidden md:block absolute left-10 right-10 top-1/2 -translate-y-1/2 h-px bg-[#0A1936]/10" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="hidden md:block absolute left-10 right-10 top-1/2 -translate-y-1/2 h-px bg-[#F5B800] origin-left"
          />
          <div className="relative grid md:grid-cols-4 gap-8">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.when}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="text-center relative"
              >
                <div
                  className={`mx-auto w-4 h-4 rounded-full ring-4 ${
                    t.highlight
                      ? "bg-[#F5B800] ring-[#F5B800]/20"
                      : "bg-[#0A1936] ring-[#0A1936]/10"
                  }`}
                />
                <div className="mt-4 font-mono uppercase tracking-[0.2em] text-[10px] text-[#0A1936]/60">
                  {t.when}
                </div>
                <div
                  className={`mt-2 font-serif text-xl sm:text-2xl leading-tight ${
                    t.highlight ? "text-[#F5B800]" : "text-[#0A1936]"
                  }`}
                >
                  {t.what}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group rounded-md border border-[#0A1936]/10 bg-white hover:bg-[#0A1936] hover:border-[#F5B800] p-6 transition-all duration-300"
              data-testid={`advance-benefit-${i}`}
            >
              <b.icon
                size={22}
                className="text-[#0A1936] group-hover:text-[#F5B800] transition-colors"
              />
              <h3 className="mt-5 font-serif text-xl text-[#0A1936] group-hover:text-white transition-colors leading-tight">
                {b.title}
              </h3>
              <p className="mt-2 text-sm text-[#0A1936]/70 group-hover:text-white/70 transition-colors leading-relaxed">
                {b.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
